/**
 * M10 P5a3a (plan 2026-10-17-m10-p5a §3.3, card P5a3a): the shipyard compare panel model. Two or three hulls side by
 * side: the flown ship first, then up to two shop rows the player picked (`onCompare(hullId)` from the row slot).
 *
 * Each column reads, per hull: band, role, maker, length, dock mode; the hold with the row's stock fit
 * (`career/hullFit.ts holdCapacityT`, plus the `holdMult` label: "bulk ×1.5"); the hull-rated shield and power
 * (`ratedStat`); top speed and turn (pitch/yaw °/s, `ships.json`); weapon hardpoints and turrets (the class row);
 * crew (`hulls.json crew`); the running cost (the captain wage if captained, `captains.json`; nothing else recurs);
 * price, doctrine and, while the gates are on, the gate status. A shop column's numbers are coloured against the
 * flown ship's (better/worse), with the direction from `derive.ts FIT_STAT_META` for the fit stats.
 *
 * The stock fit of a shop row: with a `hulls.json fit`, every slot of the career start fit at the row's
 * `stockFit.mk`, else the band's `stockMk` (`hull-lines.json sizes`); else the start fit itself (every shipped row
 * today), as P5a5a's `newOwnedShip` delivers it. A stat that does not read shows "—" (never NaN).
 *
 * Pure. Not per-frame code (built when the panel opens or a pick changes).
 */
import { z } from 'zod'
import { FIT_STAT_META } from '../../../career/derive'
import { holdCapacityT, holdMultLabel, hullFitDataFrom, hullOfDef, ratedStat, type HullFitData } from '../../../career/hullFit'
import { mergedShipsRaw } from '../../../data/shipExtends'
import { moduleId, parseModuleId, SLOT_IDS, type ModuleId, type ModuleMk, type SlotId } from '../../../data/m2Schemas'
import type { M2Data } from '../../../data/m2Schemas'
import captainsJson from '../../../data/strategic/captains.json'
import hullsJson from '../../../data/strategic/hulls.json'
import { HULL_BAND_IDS, loadHullLines } from '../../../strategic/hullLines'
import { formatCr } from '../../format'
import type { HullOffer, PanelCatalog } from './catalog'
import type { ShipyardRow } from './shipyard'

/** At most this many hulls side by side (the flown ship and two picks). */
export const COMPARE_MAX_COLUMNS = 3
/** Shown for a stat that does not read. */
export const COMPARE_MISSING = '—'

/** The panel strings (the shop strings are in `panels.json shipyard`; these are the compare panel's own). */
export const COMPARE_TEXT = Object.freeze({
  title: 'Compare hulls',
  flown: 'Flown',
  close: 'Close compare',
  remove: 'Remove',
  dock: { bay: 'Bays', moor: 'Moors', station: 'Station berth' },
  gateOpen: 'Open',
  noCaptain: 'None: you fly it',
  captainWage: (base: string): string => `Captain from ${base}/day if captained`,
})

export type CompareStatKey =
  | 'band'
  | 'role'
  | 'maker'
  | 'length'
  | 'dock'
  | 'hold'
  | 'shield'
  | 'power'
  | 'speed'
  | 'pitch'
  | 'yaw'
  | 'weapons'
  | 'turrets'
  | 'crew'
  | 'running'
  | 'price'
  | 'doctrine'
  | 'gate'

/** Row order, label and how a number compares (true: higher is better; false: lower; null: not coloured). */
const STAT_ROWS: readonly { key: CompareStatKey; label: string; higherIsBetter: boolean | null }[] = [
  { key: 'band', label: 'Band', higherIsBetter: null },
  { key: 'role', label: 'Role', higherIsBetter: null },
  { key: 'maker', label: 'Maker', higherIsBetter: null },
  { key: 'length', label: 'Length (m)', higherIsBetter: null },
  { key: 'dock', label: 'Dock mode', higherIsBetter: null },
  { key: 'hold', label: 'Hold (t, stock fit)', higherIsBetter: FIT_STAT_META.cargoCapacityT.higherIsBetter },
  { key: 'shield', label: FIT_STAT_META.shieldMult.label, higherIsBetter: FIT_STAT_META.shieldMult.higherIsBetter },
  { key: 'power', label: FIT_STAT_META.powerBudget.label, higherIsBetter: FIT_STAT_META.powerBudget.higherIsBetter },
  { key: 'speed', label: 'Top speed (m/s)', higherIsBetter: true },
  { key: 'pitch', label: 'Pitch (°/s)', higherIsBetter: true },
  { key: 'yaw', label: 'Yaw (°/s)', higherIsBetter: true },
  { key: 'weapons', label: 'Weapon hardpoints', higherIsBetter: true },
  { key: 'turrets', label: 'Turrets', higherIsBetter: true },
  { key: 'crew', label: 'Crew', higherIsBetter: null },
  { key: 'running', label: 'Running costs', higherIsBetter: null },
  { key: 'price', label: 'Price', higherIsBetter: null },
  { key: 'doctrine', label: 'Doctrine', higherIsBetter: null },
  { key: 'gate', label: 'Gate status', higherIsBetter: null },
]

/** What the model reads besides the shop: the fit resolver, the stock Mk per hull, flight numbers, the wage. */
export interface CompareData {
  hulls: HullFitData
  /** Hull id → the Mk a new one ships with (only hulls with a `fit`: `stockFit.mk`, else the band's `stockMk`). */
  stockMk: ReadonlyMap<string, ModuleMk>
  /** Ship def id → top speed (m/s) and pitch/yaw rates (°/s). */
  flight: ReadonlyMap<string, { speed: number; pitch: number; yaw: number }>
  /** The lowest captain wage per day (`captains.json wage.base`). */
  wageBase: number
  modules: Pick<M2Data, 'modules'>
}

const stockRowSchema = z.object({
  id: z.string(),
  band: z.enum(HULL_BAND_IDS).optional(),
  fit: z.unknown().optional(),
  stockFit: z.object({ mk: z.int().min(1).max(3) }).optional(),
})
const wageSchema = z.object({ wage: z.object({ base: z.number().nonnegative() }) })

function isMk(n: number): n is ModuleMk {
  return n === 1 || n === 2 || n === 3
}

/** Builds the compare data from raw `hulls.json` rows and `hull-lines.json` (tests pass copies). */
export function compareDataFrom(
  modules: Pick<M2Data, 'modules'>,
  rawHulls: readonly unknown[] = hullsJson.hulls,
  linesRaw?: unknown,
  rawCaptains: unknown = captainsJson,
): CompareData {
  const sizes = loadHullLines(linesRaw).sizes
  const stockMk = new Map<string, ModuleMk>()
  for (const raw of rawHulls) {
    const r = stockRowSchema.parse(raw)
    if (r.fit === undefined || r.band === undefined) continue
    const mk = r.stockFit?.mk ?? sizes[r.band].stockMk
    if (mk !== undefined && isMk(mk)) stockMk.set(r.id, mk)
  }
  const flight = new Map(mergedShipsRaw().map((s) => [s.id, { speed: s.maxSpeed, pitch: s.maxRateDeg.pitch, yaw: s.maxRateDeg.yaw }]))
  return {
    hulls: hullFitDataFrom(rawHulls, sizes),
    stockMk,
    flight,
    wageBase: wageSchema.parse(rawCaptains).wage.base,
    modules,
  }
}

/** The fit a new `hullId` is delivered with: the start fit at the hull's stock Mk, else the start fit as it is. */
export function compareStockFit(hullId: string, startFit: Readonly<Record<SlotId, ModuleId>>, d: Pick<CompareData, 'stockMk'>): Record<SlotId, ModuleId> {
  const mk = d.stockMk.get(hullId)
  const out = { ...startFit }
  if (mk === undefined) return out
  for (const slot of SLOT_IDS) out[slot] = moduleId(parseModuleId(startFit[slot]).kind, mk)
  return out
}

/** The picked hulls after `hullId` is toggled: a pick is removed, a new one appended (the oldest drops past the cap). */
export function toggleCompare(picks: readonly string[], hullId: string, maxPicks = COMPARE_MAX_COLUMNS - 1): string[] {
  if (picks.includes(hullId)) return picks.filter((h) => h !== hullId)
  const next = [...picks, hullId]
  return next.length > maxPicks ? next.slice(next.length - maxPicks) : next
}

export type CompareDelta = 'better' | 'worse' | 'same' | null

export interface CompareCell {
  text: string
  /** Against the flown ship's column (null: the flown column, a text stat, or either side missing). */
  delta: CompareDelta
}

export interface CompareColumn {
  /** The hull id (the flown column's hull, or the def id when no hull row lists it). */
  hull: string
  name: string
  flown: boolean
  cells: Readonly<Record<CompareStatKey, CompareCell>>
}

export interface CompareModel {
  title: string
  stats: readonly { key: CompareStatKey; label: string }[]
  columns: CompareColumn[]
}

export interface CompareInput {
  /** The flown ship (`career.ship`), or null without a Career. */
  flown: { defId: string; fitted: Readonly<Record<SlotId, ModuleId>> } | null
  /** The career start fit a new ship is delivered with (`m2.data.career.start.fitted`). */
  startFit: Readonly<Record<SlotId, ModuleId>>
  /** The shop's rows (`shipyardModel(...).rows`): price, dock line, gate block. */
  rows: readonly ShipyardRow[]
  /** The picked hull ids, oldest first. */
  picks: readonly string[]
  /** The gates are on (`shipyard.json enabled`): the gate status row shows. */
  gatesOn: boolean
}

interface Values {
  text: Partial<Record<CompareStatKey, string | null>>
  num: Partial<Record<CompareStatKey, number | null>>
}

function finite(n: number | null | undefined): number | null {
  return n === null || n === undefined || !Number.isFinite(n) ? null : n
}

/** A number for the table ("—" when it does not read; NaN never shows). */
export function compareNumber(n: number | null | undefined, digits = 0): string {
  const v = finite(n)
  if (v === null) return COMPARE_MISSING
  return digits === 0 ? String(Math.round(v)) : v.toFixed(digits)
}

function delta(a: number | null, flown: number | null, higherIsBetter: boolean | null): CompareDelta {
  if (higherIsBetter === null || a === null || flown === null) return null
  if (a === flown) return 'same'
  return a > flown === higherIsBetter ? 'better' : 'worse'
}

function hullValues(offer: HullOffer | undefined, defId: string, fitted: Readonly<Record<SlotId, ModuleId>>, d: CompareData): Values {
  const fl = d.flight.get(defId)
  const hold = finite(holdCapacityT(defId, fitted, d.modules, d.hulls))
  const mult = holdMultLabel(defId, d.hulls)
  return {
    text: {
      hold: hold === null ? null : `${compareNumber(hold)}${mult === null ? '' : ` (${mult})`}`,
      band: offer?.band ?? null,
      role: offer?.role ?? null,
      maker: offer?.maker ?? null,
      dock: offer === undefined ? null : COMPARE_TEXT.dock[offer.dockMode],
      doctrine: offer?.doctrine ?? null,
    },
    num: {
      length: offer?.lengthM ?? null,
      hold,
      shield: finite(ratedStat(defId, 'shield', fitted, d.modules, d.hulls)),
      power: finite(ratedStat(defId, 'power', fitted, d.modules, d.hulls)),
      speed: fl?.speed ?? null,
      pitch: fl?.pitch ?? null,
      yaw: fl?.yaw ?? null,
      weapons: offer?.cls.weapons ?? null,
      turrets: offer?.cls.turrets ?? null,
      crew: offer?.crew ?? null,
    },
  }
}

const DIGITS: Partial<Record<CompareStatKey, number>> = { shield: 2 }

function cellsOf(v: Values, flown: Values | null, stats: readonly (typeof STAT_ROWS)[number][]): Record<CompareStatKey, CompareCell> {
  const out = {} as Record<CompareStatKey, CompareCell>
  for (const s of stats) {
    const t = v.text[s.key]
    const n = finite(v.num[s.key])
    const text = t !== undefined ? (t ?? COMPARE_MISSING) : compareNumber(n, DIGITS[s.key] ?? 0)
    out[s.key] = { text, delta: flown === null ? null : delta(n, finite(flown.num[s.key]), s.higherIsBetter) }
  }
  return out
}

/** The compare table: the flown ship's column, then each picked shop row (unknown ids skipped). */
export function compareModel(input: CompareInput, cat: PanelCatalog, d: CompareData): CompareModel {
  const stats = STAT_ROWS.filter((s) => s.key !== 'gate' || input.gatesOn)
  const columns: CompareColumn[] = []
  let flownValues: Values | null = null
  if (input.flown !== null) {
    const hullId = hullOfDef(input.flown.defId)
    const offer = hullId === null ? undefined : cat.hullById.get(hullId)
    const v = hullValues(offer, input.flown.defId, input.flown.fitted, d)
    v.text.running = COMPARE_TEXT.noCaptain
    v.text.price = null
    v.text.gate = null
    flownValues = v
    columns.push({ hull: hullId ?? input.flown.defId, name: `${COMPARE_TEXT.flown}: ${offer?.name ?? input.flown.defId}`, flown: true, cells: cellsOf(v, null, stats) })
  }
  for (const id of input.picks.slice(0, COMPARE_MAX_COLUMNS - columns.length)) {
    const row = input.rows.find((r) => r.hull === id)
    const offer = cat.hullById.get(id)
    if (row === undefined || offer === undefined) continue
    const v = hullValues(offer, offer.shipDef, compareStockFit(id, input.startFit, d), d)
    v.text.running = COMPARE_TEXT.captainWage(formatCr(d.wageBase))
    v.text.price = row.priceLabel
    v.text.gate = row.block === 'gate' ? row.reason : COMPARE_TEXT.gateOpen
    columns.push({ hull: id, name: row.name, flown: false, cells: cellsOf(v, flownValues, stats) })
  }
  return { title: COMPARE_TEXT.title, stats: stats.map((s) => ({ key: s.key, label: s.label })), columns }
}

